import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Zap } from "lucide-react";

export default function Login() {
  const { t, login } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(errMsg(err, t("errGeneric")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-primary p-10 text-primary-foreground lg:flex">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-foreground/15"><Zap className="h-4 w-4" /></span>
          <span className="font-heading text-lg font-semibold">DomainPitch AI</span>
        </div>
        <div>
          <h2 className="font-heading text-3xl font-semibold leading-tight">{t("tagline")}</h2>
          <p className="mt-3 max-w-sm text-primary-foreground/80">{t("heroSubtitle")}</p>
        </div>
        <div className="text-sm text-primary-foreground/70">{t("stripeTrust")}</div>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center justify-between p-4">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← {t("appName")}</Link>
          <LanguageSwitcher />
        </div>
        <div className="flex flex-1 items-center justify-center px-4 pb-16">
          <Card className="w-full max-w-md p-6 sm:p-8">
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{t("loginTitle")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("loginSubtitle")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">{t("email")}</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" placeholder="you@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">{t("password")}</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" placeholder="••••••••" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                {loading ? t("loading") : t("login")}
              </Button>
            </form>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {t("noAccount")}{" "}
              <Link to="/register" className="font-medium text-primary hover:underline" data-testid="go-register-link">{t("register")}</Link>
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
